/**
 * Shared presentational kit for the admin bulk-download screens (labels / invoices).
 * Matches the admin analytics theme (primary #1565c0, see ./styled.js). Provides:
 *   - FormField     : renders the LABEL ABOVE the control so every input aligns,
 *                     regardless of control type (text / date / time / select / courier)
 *   - ToggleField   : a modern switch/pill card replacing the old MUI checkbox
 *   - modernInputsSx: drop-in sx for the form wrapper to restyle all MUI inputs
 *   - ctaBtnSx / ghostBtnSx : primary + secondary action buttons
 * Purely presentational — no submit logic lives here.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { useFormContext, Controller } from 'react-hook-form'
import { palette } from './styled'

const BLUE = palette.primary
const BLUE_DARK = '#0d47a1'
const NAVY = palette.grey800
const SUBTLE = palette.grey600
const BORDER = palette.grey200
const WHITE = palette.white

// ── Field wrapper: label ABOVE the control (uniform alignment across input types) ──
export function FormField({ label, required, hint, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      <Typography component="label" sx={{
        fontSize: '11px', fontWeight: 600, color: SUBTLE,
        textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4,
      }}>
        {label}{required && <Box component="span" sx={{ color: palette.error, ml: 0.25 }}>*</Box>}
      </Typography>
      {children}
      {hint && (
        <Typography sx={{ fontSize: '10.5px', color: palette.grey600, lineHeight: 1.3, opacity: 0.85 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}

// ── Modern toggle (replaces old MUI checkbox): a clickable card with a pill switch ──
export function ToggleField({ name, label, icon }) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const on = !!field.value
        return (
          <Box
            onClick={() => field.onChange(!on)}
            role="switch"
            aria-checked={on}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
              px: 1.5, py: 1, borderRadius: '10px', minWidth: 200, flex: '1 1 auto',
              border: `1px solid ${on ? palette.primaryBorder : BORDER}`,
              bgcolor: on ? palette.primaryLight : WHITE,
              transition: 'all 0.15s ease',
              '&:hover': { borderColor: BLUE, boxShadow: `0 2px 8px ${BLUE}1f` },
            }}
          >
            {icon && React.cloneElement(icon, { sx: { fontSize: 18, color: on ? BLUE : palette.grey600 } })}
            <Typography sx={{ flex: 1, fontSize: '12.5px', fontWeight: 500, color: on ? NAVY : SUBTLE }}>
              {label}
            </Typography>
            <Box sx={{
              width: 34, height: 20, borderRadius: '999px', flexShrink: 0, position: 'relative',
              bgcolor: on ? BLUE : '#cfd8e3', transition: 'background 0.15s ease',
            }}>
              <Box sx={{
                position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16,
                borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.28)',
                transition: 'left 0.15s ease',
              }} />
            </Box>
          </Box>
        )
      }}
    />
  )
}

// ── Drop-in sx for the form wrapper: modernises every MUI input inside it ───────
// Labels render above via FormField, so inputs carry no floating label — this
// rounds corners, gives a consistent height, and adds a clean focus ring.
export const modernInputsSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: WHITE,
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: `${BLUE}88` },
    '&.Mui-focused fieldset': { borderColor: BLUE, borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${BLUE}1f` },
  },
  '& .MuiOutlinedInput-input': { fontSize: 13, padding: '10.5px 12px' },
  '& .MuiAutocomplete-inputRoot': { paddingTop: '3px', paddingBottom: '3px' },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
}

// ── Call-to-action buttons ──────────────────────────────────────────────────────
export const ctaBtnSx = {
  fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', color: WHITE,
  px: 2.75, py: 1, borderRadius: '10px', whiteSpace: 'nowrap', boxShadow: '0 6px 16px rgba(21,101,192,0.30)',
  background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`,
  '&:hover': { background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`, filter: 'brightness(1.06)', boxShadow: '0 6px 16px rgba(21,101,192,0.30)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.8)', background: '#90a4c4' },
}

export const ghostBtnSx = {
  fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', color: SUBTLE,
  px: 2.25, py: 1, borderRadius: '10px', whiteSpace: 'nowrap', border: `1px solid ${BORDER}`,
  '&:hover': { borderColor: BLUE, bgcolor: `${BLUE}0a`, color: NAVY },
}
